import * as THREE from 'three';
import { profile } from './profile.js';
import { combine, patch, shade } from './surface.js';
import { PERSON_LANDMARKS } from './anatomy.js';

export const SKIRT_SECTIONS = [
  { y: .45, rx: .455, rz: .45 }, { y: .49, rx: .45, rz: .44 },
  { y: .67, rx: .405, rz: .37 }, { y: .83, rx: .335, rz: .28 },
  { y: .99, rx: .262, rz: .176 }, { y: 1.035, rx: .26, rz: .174 }
];
export const DRESS_SECTIONS = [
  { y: .52, rx: .48, rz: .46 }, { y: .56, rx: .475, rz: .45 },
  { y: .77, rx: .405, rz: .335 }, { y: .96, rx: .29, rz: .21 },
  { y: 1.08, rx: .251, rz: .167 }
];

function hem(rings, segments, power) {
  // The open hem exposes the entire inside, not just its first two rings.
  // Continue the lining up to the waist and close it above the legs, keeping
  // the bottom open. Both walls share the garment's existing draw/pose slot.
  const waist = rings.filter(r => r.y <= 1.08);
  const inner = waist.map((r, i) => ({ ...r,
    y: r.y - (i === waist.length - 1 ? .012 : 0),
    rx: r.rx - .015, rz: r.rz - .015
  }));
  const lining = profile(inner, { segments, power, caps: 'top' });
  for (let i = 0; i < lining.index.count; i += 3) {
    const a = lining.index.getX(i); lining.index.setX(i, lining.index.getX(i + 1)); lining.index.setX(i + 1, a);
  }
  lining.computeVertexNormals(); shade(lining, .68);
  const edge = shade(profile([inner[0],rings[0]],{segments,power,caps:'none'}),.72);
  return [lining,edge];
}

function skirt(rings) {
  const outer = profile(rings, { segments: 16, power: .65, caps: 'top' });
  const hemY = rings[0].y;
  shade(outer, (x, y, z) => y < hemY + .045 ? .78 : .96 + .04 * Math.cos(Math.atan2(x, z) * 8));
  return combine([outer, ...hem(rings,16,.65)]);
}

const TOPS = {
  regata: [[-.36,.25,.165],[-.15,.255,.17],[.14,.3,.181],[.23,.322,.165],[.31,.125,.105]],
  tshirt: [[-.38,.271,.181],[-.27,.275,.184],[-.08,.279,.19],[.17,.325,.193],[.25,.338,.176],[.33,.132,.115]],
  shirt: [[-.40,.268,.174],[-.27,.271,.178],[-.1,.264,.182],[.16,.315,.19],[.25,.335,.172],[.34,.124,.105]],
  blazer: [[-.43,.29,.192],[-.28,.29,.198],[-.10,.278,.196],[.17,.335,.213],[.26,.352,.19],[.34,.129,.113]],
  dress: [[-.30,.25,.167],[-.15,.252,.17],[.1,.29,.188],[.21,.316,.175],[.29,.127,.109]]
};

function top(type, skin, tucked = false) {
  let rings = TOPS[type].map(([y, rx, rz]) => ({ y: y + 1.36, rx, rz }));
  if (tucked) rings = [{y:1.02,rx:.254,rz:.169}, ...rings.filter(r=>r.y>1.06)];
  if (type === 'dress') rings = [...DRESS_SECTIONS.filter(r=>r.y<rings[0].y), ...rings];
  // Sample the shoulder-to-neck transition separately from the lower torso.
  // Every top ends at the base of the neck, without a lowered front opening.
  const shoulder = rings.at(-2), collar = rings.at(-1);
  rings.splice(rings.length - 1, 0, {
    y: (shoulder.y + collar.y) / 2,
    rx: (shoulder.rx + collar.rx) / 2, rz: (shoulder.rz + collar.rz) / 2
  });
  const segments = 24;
  const geo = profile(rings, { segments, power: .55, caps: type === 'dress' ? 'none' : 'bottom' });
  const pos = geo.attributes.position, rimStart = (rings.length - 1) * segments;
  const fitRay = new THREE.Raycaster(), direction = new THREE.Vector3();
  const fit = (x, y, z, opening = false) => {
    direction.set(x, 0, z).normalize();
    fitRay.set(new THREE.Vector3(direction.x, y, direction.z), direction.clone().negate());
    const hit = fitRay.intersectObject(skin)[0];
    const radius = Math.hypot(x,z), skinRadius = hit ? Math.hypot(hit.point.x,hit.point.z) : radius;
    const fitted = opening ? skinRadius + .016 : Math.max(radius, skinRadius + .016);
    return [direction.x * fitted, y, direction.z * fitted];
  };
  for (let i = 0; i < segments; i++) {
    const neckY = 1.71;
    const outerIndex = (rings.length - 3) * segments + i;
    const outerY = Math.min(Math.max(shoulder.y, 1.61), neckY - .045);
    const outer = fit(pos.getX(outerIndex), outerY, pos.getZ(outerIndex));
    const neck = fit(pos.getX(rimStart+i), neckY, pos.getZ(rimStart+i), true);
    pos.setXYZ(outerIndex, ...outer);
    pos.setXYZ(rimStart+i, ...neck);
    // Keep a section near the chest/neck junction so the last faces follow
    // its change of slope instead of cutting through the anatomical shoulder.
    pos.setXYZ(rimStart-segments+i, ...fit(...outer.map((v,j)=>THREE.MathUtils.lerp(v,neck[j],.82))));
  }
  // Lower chest rows also need clearance where the anatomical chest begins.
  for (let i = 0; i < (rings.length - 3) * segments; i++) {
    if (pos.getY(i) >= 1.49) pos.setXYZ(i, ...fit(pos.getX(i),pos.getY(i),pos.getZ(i)));
  }
  geo.computeVertexNormals();
  shade(geo, (x, y, z) => y < rings[0].y + .04 ? .87 : z < 0 ? .96 : 1);
  const neck = [];
  for (let i = 0; i <= segments; i++) {
    const j = rimStart + i % segments;
    neck.push([pos.getX(j), pos.getY(j), pos.getZ(j)]);
  }
  // Turn the cloth edge into the neck. The body/neck junction itself is the
  // finish, with no separate binding strip or contrasting neckline detail.
  const innerPoints = [], innerIndices = [];
  for (let i = 0; i < neck.length; i++) {
    const [x,y,z] = neck[i];
    const radius = Math.hypot(x,z);
    innerPoints.push([x,y,z],[x*(1-.024/radius),y-.004,z*(1-.024/radius)]);
    if (i < segments) {
      const k=i*2;
      innerIndices.push(k,k+2,k+1,k+1,k+2,k+3);
    }
  }
  const pieces = [geo, shade(patch(innerPoints,innerIndices),.96)];

  const mat = new THREE.MeshBasicMaterial(), mesh = new THREE.Mesh(geo, mat), ray = new THREE.Raycaster();
  mesh.updateMatrixWorld(true);
  const project = (x, y, offset = .012) => {
    ray.set(new THREE.Vector3(x, y, 1), new THREE.Vector3(0, 0, -1));
    const hit = ray.intersectObject(mesh)[0];
    if (!hit) throw new Error(`Garment detail outside ${type}: ${x},${y}`);
    return [x, y, hit.point.z + offset];
  };
  const panel = (xy, factor) => {
    const area = xy.reduce((sum, p, i) => sum + p[0]*xy[(i+1)%xy.length][1] - xy[(i+1)%xy.length][0]*p[1], 0);
    if (area < 0) xy = [...xy].reverse();
    const points = [], indices = [];
    const mid = (a,b) => [(a[0]+b[0])/2,(a[1]+b[1])/2];
    // Subdivide the cloth patches before projecting them: a long straight chord
    // between corners can otherwise disappear inside the curved chest.
    for (const tri of THREE.ShapeUtils.triangulateShape(xy.map(p=>new THREE.Vector2(...p)),[])) {
      const [a,b,c]=tri.map(i=>xy[i]), ab=mid(a,b), bc=mid(b,c), ca=mid(c,a);
      for (const face of [[a,ab,ca],[ab,b,bc],[ca,bc,c],[ab,bc,ca]]) {
        const k=points.length; points.push(...face.map(([x,y])=>project(x,y)));
        indices.push(k,k+1,k+2);
      }
    }
    return shade(patch(points,indices),factor);
  };
  const button = (x,y,r=.013) => {
    const g=new THREE.CircleGeometry(r,8), p=project(x,y,.019);
    return shade(g.translate(...p),.12);
  };
  if (type === 'shirt') {
    pieces.push(panel([[-.015,1.04],[.015,1.04],[.015,1.665],[-.015,1.665]],.84));
    for (const side of [-1,1]) pieces.push(panel([[side*.015,1.68],[side*.119,1.685],[side*.16,1.595],[side*.07,1.55]],.76));
    for (const y of [1.14,1.31,1.48]) pieces.push(button(0,y));
    pieces.push(panel([[-.23,1.45],[-.11,1.45],[-.11,1.435],[-.23,1.435]],.73));
  }
  if (type === 'blazer') {
    // Continue the inset up to the neck instead of ending it with a horizontal
    // bar on the chest. The underlying torso remains fully covered.
    const insetPoints = [project(0,1.22,.009)], insetIndices = [];
    for (const [y,width] of [[1.40,.04],[1.50,.065],[1.53,.072],[1.58,.083],[1.62,.093],[1.65,.10],[1.6938,.114],[1.705,.09]]) {
      const start = insetPoints.length;
      insetPoints.push(project(-width,y,.009),project(0,y,.009),project(width,y,.009));
      if (start === 1) insetIndices.push(0,start+1,start,0,start+2,start+1);
      else for (let j = 0; j < 2; j++) {
        const a = start-3+j, b = start+j;
        insetIndices.push(a,a+1,b,a+1,b+1,b);
      }
    }
    pieces.push(shade(patch(insetPoints,insetIndices),.58));
    const collarRows = [];
    const aroundNeck = (angle,y,offset) => {
      const direction = new THREE.Vector3(Math.sin(angle),0,Math.cos(angle));
      ray.set(new THREE.Vector3(direction.x,y,direction.z),direction.clone().negate());
      const hit = ray.intersectObject(mesh)[0];
      if (!hit) throw new Error('Blazer collar has no cloth support');
      return hit.point.addScaledVector(direction,offset).toArray();
    };
    for (let i = 3; i <= 21; i++) {
      const angle = i * Math.PI / 12;
      collarRows.push([aroundNeck(angle,1.704,.008),aroundNeck(angle,1.66,.012)]);
    }
    const frontRows = side => [[0,.025,1.22],[.05,.205,1.455],[.06,.184,1.50],[.077,.253,1.58]]
      .map(([inner,outer,y])=>[project(side*inner,y),project(side*outer,y)]);
    const rows = [...frontRows(1),...collarRows,...frontRows(-1).reverse()];
    const points = [], indices = [];
    for (let i = 0; i < rows.length; i++) {
      points.push(...rows[i]);
      // Extra samples only on the long front folds keep them outside the chest.
      if (i < rows.length-1 && (i < 4 || i >= rows.length-5)) {
        points.push(...rows[i].map((p,j)=>project((p[0]+rows[i+1][j][0])/2,(p[1]+rows[i+1][j][1])/2)));
      }
    }
    for (let i = 0; i < points.length/2-1; i++) {
      const k = i*2;
      indices.push(k,k+1,k+2,k+1,k+3,k+2);
    }
    // Lapels and the side/back collar share their boundary vertices: one
    // continuous folded strip, merged into the existing blazer geometry.
    pieces.push(shade(patch(points,indices),.83));
    for (const side of [-1,1]) {
      pieces.push(panel([[side*.075,1.105],[side*.25,1.14],[side*.25,1.115],[side*.075,1.08]],.68));
    }
    pieces.push(button(.02,1.16,.016));
  }
  if (type === 'dress') pieces.push(...hem(rings,segments,.55));
  mat.dispose();
  return combine(pieces);
}

function sleeve(type) {
  const short = type === 'tshirt', blazer = type === 'blazer';
  const end = short ? -.28 : -.575, width = blazer ? .111 : short ? .111 : .092;
  const geo = profile([
    {y:end,rx:width,rz:width}, {y:end+.04,rx:width+.003,rz:width+.003},
    {y:short?-.15:-.29,rx:blazer?.123:.108,rz:blazer?.124:.11},
    {y:-.025,rx:blazer?.14:.129,rz:blazer?.133:.12}, {y:.052,rx:.074,rz:.074}
  ], {segments:10,power:.8});
  shade(geo,(x,y)=>y<end+(short?.045:.085)?.78:1);
  return geo;
}

function trousers(short) {
  const geo = profile(short ? [
    {y:-.43,rx:.15,rz:.154},{y:-.385,rx:.151,rz:.154},{y:-.17,rx:.16,rz:.157},{y:0,rx:.147,rz:.15}
  ] : [
    {y:-.84,rx:.112,rz:.124},{y:-.795,rx:.115,rz:.126},{y:-.62,rx:.12,rz:.13},
    {y:-.42,rx:.13,rz:.136},{y:-.21,rx:.146,rz:.15},{y:0,rx:.144,rz:.15}
  ],{segments:10,power:.7});
  shade(geo,(x,y,z)=>y<(short?-.38:-.79)?.8:z<0?.94:1);
  return geo;
}

export function buildGarments(headGeometry) {
  const material = new THREE.MeshBasicMaterial(), skin = new THREE.Mesh(headGeometry, material);
  skin.position.y = PERSON_LANDMARKS.headY; skin.updateMatrixWorld(true);
  const garments = {
    tops: Object.fromEntries(Object.keys(TOPS).map(type=>[type,top(type,skin)])),
    tucked: Object.fromEntries(['regata','tshirt','shirt','blazer'].map(type=>[type,top(type,skin,true)])),
    sleeves: Object.fromEntries(['tshirt','shirt','blazer'].map(type=>[type,sleeve(type)])),
    pants: trousers(false), shorts: trousers(true), skirt: skirt(SKIRT_SECTIONS)
  };
  material.dispose();
  return garments;
}
